import { useState } from 'react';
import { get, put } from '../api';
import { Problems } from '../components/Field';
import { toast } from '../components/toast';
import { describe } from '../errors';
import { useLoad } from '../hooks';

type Mode = 'off' | 'auto' | 'manual';
type Channel = 'email' | 'sms';

export interface TriggerRow {
  trigger: string;
  label: string;
  description: string;
  audience: string;
  timing: 'before_visit' | 'event';
  channels: Channel[];
  modes: Mode[];
  email_mode: Mode;
  sms_mode: Mode;
  days_before: number;
  send_hour: number;
}

interface MessagingStatus {
  triggers: TriggerRow[];
  email_auto_available: boolean;
  sms_auto_available: boolean;
}

const MODE_LABEL: Record<Mode, string> = { off: 'Off', auto: 'Automatic', manual: 'Manual' };
const CHANNEL_LABEL: Record<Channel, string> = { email: 'email', sms: 'SMS' };

/** 16 -> "4:00 pm", as a person reads a time. */
export const hourLabel = (h: number) => `${h % 12 === 0 ? 12 : h % 12}:00 ${h < 12 ? 'am' : 'pm'}`;

/**
 * Which messages go out, and how: per message and channel, Automatic (sent by
 * the app with the business's own email or SMS), Manual (prepared for the
 * office to send from its own device) or Off. Visit messages also choose when.
 */
export function MessageTriggers() {
  const [tick, setTick] = useState(0);
  const status = useLoad(() => get<MessagingStatus>('/api/settings/messaging'), [tick]);
  const [edits, setEdits] = useState<Record<string, Partial<TriggerRow>>>({});
  const [problems, setProblems] = useState<string[]>([]);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const s = status.data;
  if (status.loading && !s) return null;
  if (status.error || !s || !Array.isArray(s.triggers)) return null;

  const rows = s.triggers.map((t) => ({ ...t, ...edits[t.trigger] }));
  const change = (key: string, patch: Partial<TriggerRow>) =>
    setEdits((e) => ({ ...e, [key]: { ...e[key], ...patch } }));

  async function save() {
    setProblems([]);
    setFields({});
    setSaving(true);
    try {
      await put('/api/settings/messaging', {
        triggers: rows.map((r) => ({
          trigger: r.trigger,
          email_mode: r.email_mode,
          sms_mode: r.sms_mode,
          days_before: r.days_before,
          send_hour: r.send_hour,
        })),
      });
      setEdits({});
      toast('Saved.');
      setTick((t) => t + 1);
    } catch (err) {
      const d = describe(err);
      setProblems(d.problems);
      setFields(d.fields);
    } finally {
      setSaving(false);
    }
  }

  const available = (channel: Channel) =>
    channel === 'email' ? s.email_auto_available : s.sms_auto_available;

  return (
    <section className="stack" aria-labelledby="messages-h">
      <h2 id="messages-h">Messages</h2>
      <p className="muted">
        Choose how each message goes out. Automatic messages are sent with this business&apos;s own
        email or SMS. Manual messages appear on the Dashboard under To send, and open in this
        device&apos;s email or messaging app, which decides the sender.
      </p>
      <Problems problems={problems} />
      <div className="message-triggers">
        {rows.map((r) => (
          <fieldset key={r.trigger} className="message-trigger">
            <legend>{r.label}</legend>
            {r.description && <p className="muted">{r.description}</p>}
            <div className="row wrap">
              {r.channels.map((channel) => {
                const field = `${r.trigger}.${channel}`;
                const value = channel === 'email' ? r.email_mode : r.sms_mode;
                // Invites and resets fall back to a link without a sender, so only visit messages need one.
                const noAuto =
                  r.timing === 'before_visit' && !available(channel) && r.modes.includes('auto');
                const hintId = `${field}-hint`;
                const errorId = `${field}-error`;
                return (
                  <div key={channel} className={fields[field] ? 'field invalid' : 'field'}>
                    <label>
                      {channel === 'email' ? 'Email' : 'SMS'}
                      <select
                        aria-label={`${r.label} by ${CHANNEL_LABEL[channel]}`}
                        aria-invalid={fields[field] ? true : undefined}
                        aria-describedby={
                          [noAuto ? hintId : '', fields[field] ? errorId : '']
                            .filter(Boolean)
                            .join(' ') || undefined
                        }
                        value={value}
                        onChange={(e) =>
                          change(r.trigger, {
                            [channel === 'email' ? 'email_mode' : 'sms_mode']: e.target
                              .value as Mode,
                          })
                        }
                      >
                        {r.modes.map((m) => (
                          <option
                            key={m}
                            value={m}
                            disabled={m === 'auto' && noAuto && value !== 'auto'}
                          >
                            {MODE_LABEL[m]}
                          </option>
                        ))}
                      </select>
                    </label>
                    {noAuto && (
                      <span className="muted" id={hintId}>
                        {channel === 'sms'
                          ? 'Set up SMS sending above to send automatically.'
                          : 'Set up email sending above to send automatically.'}
                      </span>
                    )}
                    {fields[field] && (
                      <span className="error" id={errorId}>
                        {fields[field]}
                      </span>
                    )}
                  </div>
                );
              })}
              {r.timing === 'before_visit' && (
                <>
                  <div className="field">
                    <label>
                      Days before
                      <select
                        aria-label={`${r.label}: days before`}
                        value={r.days_before}
                        onChange={(e) => change(r.trigger, { days_before: Number(e.target.value) })}
                      >
                        {Array.from({ length: 8 }, (_, n) => (
                          <option key={n} value={n}>
                            {n === 0 ? 'Same day' : n === 1 ? '1 day' : `${n} days`}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div className="field">
                    <label>
                      From
                      <select
                        aria-label={`${r.label}: send from`}
                        value={r.send_hour}
                        onChange={(e) => change(r.trigger, { send_hour: Number(e.target.value) })}
                      >
                        {Array.from({ length: 24 }, (_, h) => (
                          <option key={h} value={h}>
                            {hourLabel(h)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                </>
              )}
            </div>
          </fieldset>
        ))}
      </div>
      <div className="row">
        <button type="button" disabled={saving} onClick={() => void save()}>
          Save message settings
        </button>
      </div>
    </section>
  );
}
